// Linked/secondary-record corner badge — pure, d3-free helpers shared by the
// Canvas draw pass and the legend so the two never disagree. The badge replaces
// the on-node vertical-line marks (TGC-1406) with a teal corner badge that lives
// in the node's top-left whitespace, keeping the black-and-white clinical
// vocabulary inside the symbol untouched. Rendering is gated by the "Record
// status" Labels toggle (labelsSet.showRecordStatus, default off).
//
// The linkage role is DERIVED at render time from the PersonLink batch status
// map (usePersonLinkBatch); no field is persisted on the pedigree. Absence of
// linkage data yields 'none' — linkage is never inferred from missing data.

/**
 * @typedef {'primary'|'secondary'|'none'} LinkedRecordRole
 */

/**
 * MUI "Link" (InsertLink) icon geometry, 24x24 viewBox. Lifted as a local
 * constant rather than importing the React icon component — the diagram is
 * hand-built SVG and there is no precedent for rendering MUI components onto it.
 */
export const LINK_GLYPH_PATH =
  'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z';

/** Nominal side of the MUI icon viewBox, used to scale the glyph. */
const GLYPH_VIEWBOX = 24;

/**
 * Normalise a batch-map value to its role string. Accepts today's bare status
 * string and a future { status, count } object (Phase 2) transparently.
 * @param {unknown} raw
 * @returns {string|undefined}
 */
function statusOf(raw) {
  return typeof raw === 'string' ? raw : raw && raw.status;
}

/**
 * Null-safe resolution of the linked-record badge state for a single node.
 * @param {unknown} raw the batch-map entry for this node (string | object | nullish)
 * @returns {{ role: LinkedRecordRole, count: number|null }}
 */
export function resolveLinkedRecordBadge(raw) {
  const status = statusOf(raw);
  if (status !== 'primary' && status !== 'secondary') {
    return { role: 'none', count: null };
  }
  const count =
    raw &&
    typeof raw === 'object' &&
    typeof raw.count === 'number' &&
    raw.count > 1
      ? raw.count
      : null;
  return { role: status, count };
}

/**
 * Which record roles are present anywhere on the pedigree — drives per-role
 * legend visibility so a family with (say) a primary but no secondary shows only
 * the primary swatch, and a family with no links shows neither.
 * @param {Record<string, unknown>|null|undefined} linkStatusMap
 * @returns {{ hasPrimary: boolean, hasSecondary: boolean }}
 */
export function linkedRecordPresence(linkStatusMap) {
  const values = linkStatusMap ? Object.values(linkStatusMap) : [];
  let hasPrimary = false;
  let hasSecondary = false;
  for (const v of values) {
    const s = statusOf(v);
    if (s === 'primary') hasPrimary = true;
    else if (s === 'secondary') hasSecondary = true;
    if (hasPrimary && hasSecondary) break;
  }
  return { hasPrimary, hasSecondary };
}

/**
 * Badge geometry in node-local SVG units, relative to the symbol size so it
 * tracks the user's symbol-size / auto-spacing preference and zoom. Anchored at
 * the top-left corner (top-right is occupied by the +/- genotype text, proxy
 * "P", nomenclature number and deceased-slash tip). The default anchor clears
 * the symbol/carrier band and the deceased slash but sits ON the top-left tenants
 * (the cascade badge at (-0.52S,-0.52S) r 0.3S, and the adoption bracket stem at
 * x=-0.8S), so it nudges further out when either shares the corner. Every number
 * the positional test and the draw pass rely on lives here, so they cannot drift.
 * @param {number} S config.SYMBOL_SIZE
 * @param {{ hasCascadeBadge?: boolean, hasAdoptionBracket?: boolean }} [occupancy]
 * @returns {{ cx: number, cy: number, side: number, rx: number,
 *            glyphSize: number, glyphScale: number, strokeWidth: number }}
 */
export function linkedRecordBadgeGeometry(S, occupancy = {}) {
  const nudged = Boolean(
    occupancy.hasCascadeBadge || occupancy.hasAdoptionBracket
  );
  const factor = nudged ? 1.25 : 0.85;
  const side = 0.4 * S;
  const glyphSize = 0.26 * S;
  return {
    cx: -factor * S,
    cy: -factor * S,
    side,
    rx: 0.08 * S,
    glyphSize,
    glyphScale: glyphSize / GLYPH_VIEWBOX,
    strokeWidth: 0.045 * S
  };
}

/**
 * SVG transform that centres the 24x24 link glyph on (cx, cy) at the given scale.
 * @param {{ cx: number, cy: number, glyphScale: number }} geom
 * @returns {string}
 */
export function linkedRecordGlyphTransform(geom) {
  const half = (GLYPH_VIEWBOX * geom.glyphScale) / 2;
  return `translate(${geom.cx - half}, ${geom.cy - half}) scale(${
    geom.glyphScale
  })`;
}

/**
 * British-English accessible label / tooltip text for the badge. The count arg
 * is honoured now so Phase 2 (instance counts from an extended batch payload)
 * needs no signature change; Phase 1 always passes null.
 * @param {LinkedRecordRole} role
 * @param {number|null} [count]
 * @returns {string}
 */
export function linkedRecordBadgeLabel(role, count = null) {
  if (role === 'primary') {
    const of =
      typeof count === 'number' && count > 1 ? ` of ${count} instances` : '';
    return `Linked record — primary${of}. Data entry occurs on this record. Activate to view linked records.`;
  }
  if (role === 'secondary') {
    return 'Linked record — secondary (read-only copy). Activate to view the primary record.';
  }
  return '';
}
